import { addDays, dateRange, daysBetween, type LocalDate, localDateOf } from './dates.js'
import { CAPS, clampShine, levelFor, REWARDS, starsToNext, tierFor } from './economy.js'
import { type AnyEvent, compareEvents } from './events.js'
import { type DailyGorevler, dailyGorevler } from './gorev.js'
import {
  type AgeBand,
  BADGE_IDS,
  type BadgeId,
  type DecorSlot,
  GAME_IDS,
  type GameId,
  type GorevId,
  LESSON_IDS,
  type LessonId,
  type Mode,
  type Slot,
  STATIC_RECAP_QUESTION_IDS,
  type StaticRecapQuestionId,
  type WindowState,
} from './ids.js'
import { computeStreak, milestoneRewards, streakDays } from './streak.js'

/**
 * fold(events) -> ChildState. Pure, deterministic, the only source of child game state
 * (ARCHITECTURE §4). Every rule in §4.5 has a named test in test/fold.test.ts.
 */

export type WindowPair = { morning: WindowState; evening: WindowState }

export type LessonState = {
  started: boolean
  completed: boolean
  quizFirstTryCorrect: boolean | null
  chestOpened: boolean
}

export type ChildState = {
  profile: {
    nickname: string
    ageBand: AgeBand
    mode: Mode
    avatarId: string
    petName: string
    reviewMode: boolean
  }
  stars: number
  level: number
  starsToNext: number
  parilti: number
  shine: number
  streak: {
    current: number
    best: number
    freezeTokens: number
    lastVerifiedDate: string | null
    /** Days a Dondurucu covered. The app emits `streak_freeze_used` for these (§4.3). */
    freezeUsedDates: string[]
  }
  today: {
    date: string
    morning: WindowState
    evening: WindowState
    extraCount: number
    minigamePlays: number
    fedCount: number
    tamGun: boolean
    firstOpenRewarded: boolean
  }
  calendar: Record<string, WindowPair>
  lessons: Record<LessonId, LessonState>
  /** Hatırlatma Eğitimi (W3-OVERHAUL §3.2). */
  recap: {
    /** All six chests open. */
    unlocked: boolean
    completedCount: number
    /** The last completion that paid; a replay inside the cooldown does not move it. */
    lastCompletedDate: LocalDate | null
    /** `lastCompletedDate` + 7, null when never done. */
    nextRewardDate: LocalDate | null
    questions: Record<StaticRecapQuestionId, { firstTryCorrect: boolean | null }>
  }
  /** Günlük Görevler, open from day one (W3-OVERHAUL §10). */
  gorev: {
    today: {
      date: LocalDate
      items: DailyGorevler
      done: GorevId[]
      ucluRewarded: boolean
    }
    /** Tasks done over every date, for the parent panel. */
    totalDone: number
  }
  minigames: Record<GameId, { tutorialDone: boolean; bestScore: number }>
  inventory: { foods: Record<string, number>; items: string[] }
  equipped: Partial<Record<Slot, string>>
  decor: Partial<Record<DecorSlot, string>>
  badges: BadgeId[]
  observations: Record<string, { thumb: boolean; mouthBreath: boolean; tongueThrust: boolean }>
  lastEventTs: number
}

export type CatalogEntry = {
  kind: 'food' | 'cosmetic' | 'decor'
  price: number | null
  slot?: Slot
  /** Region rewards and Plus items are not buyable with Parıltı. */
  purchasable: boolean
}

export type Catalog = {
  items: Record<string, CatalogEntry>
  /** The cosmetic each region chest grants (CONTENT §2 `reward.cosmetic`). */
  lessonCosmetic: Partial<Record<LessonId, string>>
}

export const EMPTY_CATALOG: Catalog = { items: {}, lessonCosmetic: {} }

export type FoldContext = {
  /** The child's local date now. Defaults to the latest date seen in the events. */
  today?: LocalDate
  /** Seeds the görev generator. Defaults to the events' own `childId`. */
  childId?: string
  catalog?: Catalog
  /** Called for a row the fold refuses (unknown type, unaffordable purchase, bad id). */
  onIssue?: (issue: { reason: string; eventId: string; type: string }) => void
}

/** Window rank. `nocam` never overrides a better state (fold rule 2). */
const WINDOW_RANK: Record<WindowState, number> = {
  none: 0,
  nocam: 1,
  tried: 2,
  partial: 3,
  verified: 4,
}

const WINDOW_REWARD: Record<WindowState, { stars: number; parilti: number }> = {
  none: { stars: 0, parilti: 0 },
  nocam: { stars: 0, parilti: 0 },
  tried: { stars: REWARDS.tried.stars, parilti: REWARDS.tried.parilti },
  partial: { stars: REWARDS.partial.stars, parilti: REWARDS.partial.parilti },
  verified: { stars: REWARDS.verified.stars, parilti: REWARDS.verified.parilti },
}

/** Fresh pets are shiny: a new profile starts Parlak (MASTERPLAN §7.1 band 75 to 100). */
const INITIAL_SHINE = 100

const SHINE_BY_OUTCOME = {
  verified: REWARDS.verified.shine,
  partial: REWARDS.partial.shine,
  tried: REWARDS.tried.shine,
  nocam: REWARDS.nocam.shine,
} as const

/** The fold needs one more bit than ChildState exposes: whether the quiz already paid out. */
type LessonAccum = LessonState & { quizRewarded: boolean }

function emptyLessons(): Record<LessonId, LessonAccum> {
  const out = {} as Record<LessonId, LessonAccum>
  for (const id of LESSON_IDS) {
    out[id] = {
      started: false,
      completed: false,
      quizFirstTryCorrect: null,
      chestOpened: false,
      quizRewarded: false,
    }
  }
  return out
}

function stripInternals(lessons: Record<LessonId, LessonAccum>): Record<LessonId, LessonState> {
  const out = {} as Record<LessonId, LessonState>
  for (const id of LESSON_IDS) {
    const { quizRewarded: _quizRewarded, ...rest } = lessons[id]
    out[id] = rest
  }
  return out
}

function emptyMinigames(): Record<GameId, { tutorialDone: boolean; bestScore: number }> {
  const out = {} as Record<GameId, { tutorialDone: boolean; bestScore: number }>
  for (const id of GAME_IDS) out[id] = { tutorialDone: false, bestScore: 0 }
  return out
}

export function fold(events: readonly AnyEvent[], context: FoldContext = {}): ChildState {
  const catalog = context.catalog ?? EMPTY_CATALOG
  const onIssue = context.onIssue
  const issue = (reason: string, event: AnyEvent) =>
    onIssue?.({ reason, eventId: event.id, type: event.type })

  const seen = new Set<string>()
  const ordered = [...events]
    .filter((event) => {
      if (seen.has(event.id)) return false
      seen.add(event.id)
      return true
    })
    .sort(compareEvents)

  let nickname = ''
  let ageBand: AgeBand = '8-12'
  let avatarId = ''
  let petName = ''
  let modeOverride: Mode | null = null
  let reviewMode = false

  let stars = 0
  let parilti = 0
  let shine = INITIAL_SHINE
  let lastEventTs = 0

  const calendar: Record<string, WindowPair> = {}
  const extraByDate: Record<string, number> = {}
  const feedsByDate: Record<string, number> = {}
  const playsByDate: Record<string, number> = {}
  const openedDates = new Set<string>()
  const closedWindows = new Set<string>()
  const tamGunDates = new Set<string>()

  const lessons = emptyLessons()
  const minigames = emptyMinigames()
  const recapQuestions = emptyRecapQuestions()
  let recapCompletedCount = 0
  let lastRecapRewardDate: LocalDate | null = null
  /** Dates with a correct `gorev_answered` (the question task) or a `lesson_completed`. */
  const gorevQuestionDates = new Set<string>()
  const lessonCompletedDates = new Set<string>()
  /** Best score per game per date, tutorial plays included (the game task counts them). */
  const gameBestByDate: Record<string, Partial<Record<GameId, number>>> = {}
  const observations: ChildState['observations'] = {}
  const foods: Record<string, number> = {}
  const items: string[] = []
  const owned = new Set<string>()
  const equipped: Partial<Record<Slot, string>> = {}
  const decor: Partial<Record<DecorSlot, string>> = {}

  let verifiedCount = 0
  let feedCount = 0
  let lastVerifiedTs: number | null = null
  let firstDate: LocalDate | null = null

  const dayOf = (date: string): WindowPair => {
    const existing = calendar[date]
    if (existing) return existing
    const created: WindowPair = { morning: 'none', evening: 'none' }
    calendar[date] = created
    return created
  }

  for (const event of ordered) {
    if (event.ts > lastEventTs) lastEventTs = event.ts
    if (firstDate === null || event.localDate < firstDate) firstDate = event.localDate

    switch (event.type) {
      case 'profile_created': {
        nickname = event.payload.nickname
        ageBand = event.payload.ageBand
        avatarId = event.payload.avatarId
        petName = event.payload.petName
        modeOverride = null
        break
      }
      case 'profile_updated': {
        const p = event.payload
        if (p.nickname !== undefined) nickname = p.nickname
        if (p.avatarId !== undefined) avatarId = p.avatarId
        if (p.petName !== undefined) petName = p.petName
        // A new age band re-derives the mode; the parent can switch it again afterwards.
        if (p.ageBand !== undefined) {
          ageBand = p.ageBand
          modeOverride = null
        }
        break
      }
      case 'pet_renamed': {
        petName = event.payload.petName
        break
      }
      case 'mode_changed': {
        modeOverride = event.payload.mode
        break
      }
      case 'review_mode_set': {
        reviewMode = event.payload.on
        break
      }
      case 'app_opened': {
        // Rule 12: the first open of a local date is worth +5 Parıltı, once.
        if (!openedDates.has(event.localDate)) {
          openedDates.add(event.localDate)
          parilti += REWARDS.dailyFirstOpen.parilti
        }
        break
      }
      case 'brush_session': {
        const { window, outcome } = event.payload
        const day = dayOf(event.localDate)
        // Rule 2: outside both windows is always ekstra, shine only, never stars.
        if (window === 'outside') {
          extraByDate[event.localDate] = (extraByDate[event.localDate] ?? 0) + 1
          shine = clampShine(shine + REWARDS.extra.shine)
          break
        }
        const current = day[window]
        const tooSoon =
          outcome === 'verified' &&
          lastVerifiedTs !== null &&
          event.ts - lastVerifiedTs < CAPS.minVerifiedSpacingMs
        // A window that is already verified, or a verified inside the 4 hour spacing, is ekstra.
        if (current === 'verified' || tooSoon) {
          extraByDate[event.localDate] = (extraByDate[event.localDate] ?? 0) + 1
          shine = clampShine(shine + REWARDS.extra.shine)
          break
        }

        shine = clampShine(shine + SHINE_BY_OUTCOME[outcome])
        if (WINDOW_RANK[outcome] > WINDOW_RANK[current]) {
          // Upgrades pay the difference, so a window is worth its best outcome and no more.
          stars += WINDOW_REWARD[outcome].stars - WINDOW_REWARD[current].stars
          parilti += WINDOW_REWARD[outcome].parilti - WINDOW_REWARD[current].parilti
          day[window] = outcome
        }
        if (outcome === 'verified') {
          verifiedCount += 1
          lastVerifiedTs = event.ts
          // Rule 3: Tam Gün once per date, when both windows are verified.
          if (
            day.morning === 'verified' &&
            day.evening === 'verified' &&
            !tamGunDates.has(event.localDate)
          ) {
            tamGunDates.add(event.localDate)
            stars += REWARDS.tamGun.stars
            parilti += REWARDS.tamGun.parilti
          }
        }
        break
      }
      case 'window_closed': {
        const key = `${event.payload.localDate}:${event.payload.window}`
        if (closedWindows.has(key)) break
        closedWindows.add(key)
        const day = dayOf(event.payload.localDate)
        // Rule 5. TODO(open:window-close-penalty) ARCHITECTURE §4.5 says "not verified" while
        // MASTERPLAN §7.1 says "no brush"; a partial window is penalised under the rule used here.
        if (day[event.payload.window] !== 'verified') {
          shine = clampShine(shine + REWARDS.windowMissed.shine)
        }
        break
      }
      case 'lesson_started': {
        lessons[event.payload.lessonId].started = true
        break
      }
      case 'lesson_completed': {
        const lesson = lessons[event.payload.lessonId]
        if (lesson.completed) break
        lesson.completed = true
        lesson.started = true
        lessonCompletedDates.add(event.localDate)
        stars += REWARDS.lessonCompleted.stars
        parilti += REWARDS.lessonCompleted.parilti
        break
      }
      case 'quiz_answered': {
        const lesson = lessons[event.payload.lessonId]
        if (lesson.quizFirstTryCorrect === null && event.payload.attempt === 1) {
          lesson.quizFirstTryCorrect = event.payload.correct
        }
        if (!event.payload.correct) break
        // Rule 6: the quiz pays once per lesson, more for a first try.
        if (lesson.quizRewarded) break
        lesson.quizRewarded = true
        const reward = event.payload.attempt === 1 ? REWARDS.quizFirstTry : REWARDS.quizLaterTry
        stars += reward.stars
        parilti += reward.parilti
        break
      }
      case 'chest_opened': {
        const lessonId = event.payload.lessonId
        const lesson = lessons[lessonId]
        if (lesson.chestOpened) break
        lesson.chestOpened = true
        const cosmetic = catalog.lessonCosmetic[lessonId]
        if (cosmetic && !owned.has(cosmetic)) {
          owned.add(cosmetic)
          items.push(cosmetic)
        }
        break
      }
      case 'minigame_played': {
        const { gameId, score, tutorial } = event.payload
        const game = minigames[gameId]
        if (score > game.bestScore) game.bestScore = score
        const dayBest = gameBestByDate[event.localDate] ?? {}
        if (score > (dayBest[gameId] ?? -1)) dayBest[gameId] = score
        gameBestByDate[event.localDate] = dayBest
        if (tutorial) {
          // Rule 8: the tutorial run pays 15 once per game and never eats a daily play.
          if (!game.tutorialDone) {
            game.tutorialDone = true
            parilti += REWARDS.minigameTutorial.parilti
          }
          break
        }
        const played = playsByDate[event.localDate] ?? 0
        if (played < CAPS.rewardedMinigamePlaysPerDay) {
          playsByDate[event.localDate] = played + 1
          parilti += tierFor(gameId, score)
        }
        break
      }
      case 'pet_fed': {
        // Rule 9: two feeds a day, each consumes one food from the inventory.
        const fed = feedsByDate[event.localDate] ?? 0
        if (fed >= CAPS.feedsPerDay) break
        const stock = foods[event.payload.foodId] ?? 0
        if (stock <= 0) {
          issue('no such food in the inventory', event)
          break
        }
        foods[event.payload.foodId] = stock - 1
        feedsByDate[event.localDate] = fed + 1
        feedCount += 1
        shine = clampShine(shine + REWARDS.feed.shine)
        break
      }
      case 'item_purchased': {
        // Rule 10: the fold refuses what the client should never have created.
        const entry = catalog.items[event.payload.itemId]
        if (!entry) {
          issue('unknown item', event)
          break
        }
        if (!entry.purchasable || entry.price === null) {
          issue('item is not purchasable', event)
          break
        }
        if (entry.kind !== 'food' && owned.has(event.payload.itemId)) {
          issue('item already owned', event)
          break
        }
        if (parilti < entry.price) {
          issue('not enough parilti', event)
          break
        }
        parilti -= entry.price
        if (entry.kind === 'food') {
          foods[event.payload.itemId] = (foods[event.payload.itemId] ?? 0) + 1
        } else {
          owned.add(event.payload.itemId)
          items.push(event.payload.itemId)
        }
        break
      }
      case 'item_equipped': {
        const { slot, itemId } = event.payload
        if (itemId === null) {
          delete equipped[slot]
          break
        }
        if (!owned.has(itemId)) {
          issue('item not owned', event)
          break
        }
        equipped[slot] = itemId
        break
      }
      case 'decor_placed': {
        const { slot, itemId } = event.payload
        if (itemId === null) {
          delete decor[slot]
          break
        }
        if (!owned.has(itemId)) {
          issue('item not owned', event)
          break
        }
        decor[slot] = itemId
        break
      }
      case 'parent_observation': {
        observations[event.payload.month] = {
          thumb: event.payload.thumb,
          mouthBreath: event.payload.mouthBreath,
          tongueThrust: event.payload.tongueThrust,
        }
        break
      }
      case 'streak_freeze_used': {
        // A research marker for a fold derived fact. It changes no amount (rule 13).
        break
      }
      case 'recap_started': {
        break
      }
      case 'recap_answered': {
        // Rule 14: the first attempt at a question is recorded once, as for a quiz.
        const questionId = event.payload.questionId
        if (!isStaticRecapQuestionId(questionId)) break
        const entry = recapQuestions[questionId]
        if (entry.firstTryCorrect === null && event.payload.attempt === 1) {
          entry.firstTryCorrect = event.payload.correct
        }
        break
      }
      case 'recap_completed': {
        // Rule 14: pays once per completion on the 7 day cooldown; a replay inside it pays
        // nothing and does not move the cooldown.
        recapCompletedCount += 1
        const date = event.payload.localDate
        if (
          lastRecapRewardDate === null ||
          daysBetween(lastRecapRewardDate, date) >= CAPS.recapCooldownDays
        ) {
          lastRecapRewardDate = date
          stars += REWARDS.recap.stars
          parilti += REWARDS.recap.parilti
        }
        break
      }
      case 'gorev_answered': {
        // Rule 15: a correct answer finishes the day's question task, once per date. The
        // Parıltı is paid with the other tasks at fold end.
        if (event.payload.correct) gorevQuestionDates.add(event.payload.localDate)
        break
      }
      default: {
        issue('unknown event type', event as AnyEvent)
        break
      }
    }
  }

  const today =
    context.today ??
    (ordered.length > 0 ? maxDate(ordered) : localDateOf(lastEventTs || Date.now()))
  const start = firstDate ?? today

  const streakResult = computeStreak(streakDays(calendar, start, today), today)
  for (const milestone of milestoneRewards(streakResult.best)) {
    stars += milestone.stars
    parilti += milestone.parilti
  }

  const mode: Mode = modeOverride ?? (ageBand === '5-7' ? 'mini' : 'core')
  const childId = context.childId ?? ordered[0]?.childId ?? ''
  const lessonsDone = LESSON_IDS.filter((id) => lessons[id].completed)
  // Rule 15: every date from the first event to today gets its three tasks and their state.
  const gorevRewards = deriveGorevler({
    childId,
    dates: dateRange(start, today),
    lessonsDone,
    mode,
    calendar,
    gorevQuestionDates,
    lessonCompletedDates,
    gameBestByDate,
  })
  stars += gorevRewards.stars
  parilti += gorevRewards.parilti

  const badges = evaluateBadges({
    streakBest: streakResult.best,
    verifiedCount,
    tamGunCount: tamGunDates.size,
    lessons,
    minigames,
    feedCount,
    cosmeticsOwned: items.filter((id) => catalog.items[id]?.kind === 'cosmetic').length,
    decorFilled: Object.values(decor).filter(Boolean).length,
    recapCompletedCount,
  })
  parilti += badges.length * REWARDS.badgeUnlock.parilti

  const todayPair = calendar[today] ?? { morning: 'none', evening: 'none' }
  const level = levelFor(stars)

  return {
    profile: {
      nickname,
      ageBand,
      mode,
      avatarId,
      petName,
      reviewMode,
    },
    stars,
    level,
    starsToNext: starsToNext(stars),
    parilti,
    shine,
    streak: {
      current: streakResult.current,
      best: streakResult.best,
      freezeTokens: streakResult.freezeTokens,
      lastVerifiedDate: streakResult.lastVerifiedDate,
      freezeUsedDates: streakResult.freezeUsedDates,
    },
    today: {
      date: today,
      morning: todayPair.morning,
      evening: todayPair.evening,
      extraCount: extraByDate[today] ?? 0,
      minigamePlays: playsByDate[today] ?? 0,
      fedCount: feedsByDate[today] ?? 0,
      tamGun: tamGunDates.has(today),
      firstOpenRewarded: openedDates.has(today),
    },
    calendar: trimCalendar(calendar, today),
    lessons: stripInternals(lessons),
    recap: {
      unlocked: LESSON_IDS.every((id) => lessons[id].chestOpened),
      completedCount: recapCompletedCount,
      lastCompletedDate: lastRecapRewardDate,
      nextRewardDate:
        lastRecapRewardDate === null ? null : addDays(lastRecapRewardDate, CAPS.recapCooldownDays),
      questions: recapQuestions,
    },
    gorev: {
      today: gorevRewards.today,
      totalDone: gorevRewards.totalDone,
    },
    minigames,
    inventory: { foods, items },
    equipped,
    decor,
    badges,
    observations,
    lastEventTs,
  }
}

function maxDate(events: readonly AnyEvent[]): LocalDate {
  let latest = events[0]?.localDate ?? localDateOf()
  for (const event of events) if (event.localDate > latest) latest = event.localDate
  return latest
}

function trimCalendar(
  calendar: Record<string, WindowPair>,
  today: LocalDate,
): Record<string, WindowPair> {
  const from = addDays(today, -(CAPS.calendarDays - 1))
  const out: Record<string, WindowPair> = {}
  for (const [date, pair] of Object.entries(calendar)) {
    if (date >= from && daysBetween(date, today) >= 0) out[date] = pair
  }
  return out
}

function emptyRecapQuestions(): ChildState['recap']['questions'] {
  const out = {} as ChildState['recap']['questions']
  for (const id of STATIC_RECAP_QUESTION_IDS) out[id] = { firstTryCorrect: null }
  return out
}

function isStaticRecapQuestionId(value: string): value is StaticRecapQuestionId {
  return (STATIC_RECAP_QUESTION_IDS as readonly string[]).includes(value)
}

type GorevInput = {
  childId: string
  dates: readonly LocalDate[]
  lessonsDone: readonly LessonId[]
  mode: Mode
  calendar: Record<string, WindowPair>
  gorevQuestionDates: Set<string>
  lessonCompletedDates: Set<string>
  gameBestByDate: Record<string, Partial<Record<GameId, number>>>
}

/**
 * Rule 15: the görevler of every date and what they paid. Brush and game tasks come from the
 * calendar and the day's best scores, the question task from a correct `gorev_answered` (or
 * a `lesson_completed` for the `ders` fallback). Each done task pays once per date, the
 * Üçlü once per date when all three are done. The last date is today.
 */
function deriveGorevler(input: GorevInput): {
  stars: number
  parilti: number
  totalDone: number
  today: ChildState['gorev']['today']
} {
  let stars = 0
  let parilti = 0
  let totalDone = 0
  let today: ChildState['gorev']['today'] | null = null

  for (const date of input.dates) {
    const items = dailyGorevler(input.childId, date, input.lessonsDone, { mode: input.mode })
    const [brush, question, game] = items
    const day = input.calendar[date] ?? { morning: 'none', evening: 'none' }
    const done: GorevId[] = []

    const brushDone =
      brush.window === 'both'
        ? day.morning === 'verified' && day.evening === 'verified'
        : day[brush.window] === 'verified'
    if (brushDone) done.push(brush.id)

    const questionDone =
      question.id === 'g-soru'
        ? input.gorevQuestionDates.has(date)
        : input.lessonCompletedDates.has(date)
    if (questionDone) done.push(question.id)

    const best = input.gameBestByDate[date]?.[game.gameId] ?? -1
    if (best >= game.target) done.push(game.id)

    totalDone += done.length
    parilti += done.length * REWARDS.gorevItem.parilti
    stars += done.length * REWARDS.gorevItem.stars
    const ucluRewarded = done.length === CAPS.gorevPerDay
    if (ucluRewarded) {
      stars += REWARDS.gorevUclu.stars
      parilti += REWARDS.gorevUclu.parilti
    }
    today = { date, items, done, ucluRewarded }
  }

  const last = input.dates.at(-1) ?? localDateOf()
  return {
    stars,
    parilti,
    totalDone,
    today: today ?? {
      date: last,
      items: dailyGorevler(input.childId, last, input.lessonsDone, { mode: input.mode }),
      done: [],
      ucluRewarded: false,
    },
  }
}

type BadgeInput = {
  streakBest: number
  verifiedCount: number
  tamGunCount: number
  lessons: Record<LessonId, LessonState>
  minigames: Record<GameId, { tutorialDone: boolean; bestScore: number }>
  feedCount: number
  cosmeticsOwned: number
  decorFilled: number
  recapCompletedCount: number
}

/** Rule 11: evaluated at fold end in BADGE_IDS order, +25 Parıltı each, once. */
const BADGE_RULES: Record<BadgeId, (input: BadgeInput) => boolean> = {
  'ilk-firca': (s) => s.verifiedCount >= 1,
  'seri-3': (s) => s.streakBest >= 3,
  'seri-7': (s) => s.streakBest >= 7,
  'seri-14': (s) => s.streakBest >= 14,
  'seri-30': (s) => s.streakBest >= 30,
  'seri-60': (s) => s.streakBest >= 60,
  'seri-100': (s) => s.streakBest >= 100,
  'tam-gun-7': (s) => s.tamGunCount >= 7,
  'firca-10': (s) => s.verifiedCount >= 10,
  'firca-50': (s) => s.verifiedCount >= 50,
  'firca-100': (s) => s.verifiedCount >= 100,
  'firca-365': (s) => s.verifiedCount >= 365,
  'ders-p1': (s) => s.lessons.p1.chestOpened,
  'ders-p2': (s) => s.lessons.p2.chestOpened,
  'ders-p3': (s) => s.lessons.p3.chestOpened,
  'ders-o1': (s) => s.lessons.o1.chestOpened,
  'ders-o2': (s) => s.lessons.o2.chestOpened,
  'ders-o3': (s) => s.lessons.o3.chestOpened,
  bilgin: (s) => LESSON_IDS.every((id) => s.lessons[id].quizFirstTryCorrect === true),
  avci: (s) => s.minigames.sekerci.bestScore >= 300,
  gurme: (s) => s.minigames.disdostu.bestScore >= 160,
  dedektif: (s) => s.minigames.dogruyanlis.bestScore >= 100,
  ritimci: (s) => s.minigames.firtina.bestScore >= 900,
  baloncu: (s) => s.minigames.nefes.bestScore >= 150,
  bakici: (s) => s.feedCount >= 20,
  modaci: (s) => s.cosmeticsOwned >= 5,
  dekorator: (s) => s.decorFilled >= 4,
  hatirlatici: (s) => s.recapCompletedCount >= 1,
}

export function evaluateBadges(input: BadgeInput): BadgeId[] {
  return BADGE_IDS.filter((id) => BADGE_RULES[id](input))
}
